<script setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import InputError from "@/Components/InputError.vue";
import InputLabel from "@/Components/InputLabel.vue";
import FormSection from "@/Components/FormSection.vue";
import TextInput from "@/Components/TextInput.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import SearchSelect from "@/Components/SearchSelect.vue";
import PhoneInput from "@/Components/PhoneInput.vue";

import { useForm } from "@inertiajs/inertia-vue3";
import { Inertia } from "@inertiajs/inertia";
import { ref, computed, watch } from "vue";

const props = defineProps({
  lead: Object,
  projectManagers: Array,
  readOnly: { type: Boolean, default: false },
});

const saving = ref(false);

const form = useForm({
  _method: props.lead?.id ? "PUT" : "POST",

  employeeID: props.lead?.employeeID != null && props.lead?.employeeID !== ''
    ? Number(props.lead.employeeID)
    : null,

  client: props.lead?.client || "",
  contact_name: props.lead?.contact_name || "",
  contact_surname: props.lead?.contact_surname || "",
  contact_number: props.lead?.contact_number || "",
  contact_email: props.lead?.contact_email || "",

  start_date: props.lead?.start_date ?? null,

  date_due: props.lead?.date_due ?? null,
  action_plan: props.lead?.action_plan || "",
  notes: props.lead?.notes || "",

  successful_lead: props.lead?.successful_lead ?? false,
  date_successful_lead: props.lead?.date_successful_lead ?? null,

  status: props.lead?.status ?? true,

  image: null,
  remove_image: false,
});

// -------------------- PM SearchSelect --------------------
const pmOptions = computed(() =>
  (props.projectManagers || [])
    .filter((manager) => manager?.employeeID != null)
    .map((manager) => ({
      label:
        `${(manager.firstname || "").trim()} ${(manager.lastname || "").trim()}`.trim() ||
        `#${manager.employeeID}`,
      value: Number(manager.employeeID),
    }))
);

const pmModel = computed({
  get: () =>
    form.employeeID != null && form.employeeID !== ""
      ? Number(form.employeeID)
      : null,
  set: (v) => {
    form.employeeID = v == null ? "" : Number(v);
  },
});

// -------------------- Date helpers (linux ts seconds) --------------------
const pad2 = (n) => String(n).padStart(2, "0");

const tsToInputDate = (ts) => {
  if (!ts) return "";
  const d = new Date(Number(ts) * 1000);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const inputDateToTs = (yyyyMMdd) => {
  const s = (yyyyMMdd || "").toString().trim();
  if (!s) return null;

  const [y, m, d] = s.split("-").map((x) => Number(x));
  if (!y || !m || !d) return null;

  const date = new Date(y, m - 1, d, 12, 0, 0);
  const ts = Math.floor(date.getTime() / 1000);
  return Number.isFinite(ts) ? ts : null;
};

const startDateInput = computed({
  get: () => tsToInputDate(form.start_date),
  set: (v) => {
    form.start_date = inputDateToTs(v);
  },
});

const dateDueInput = computed({
  get: () => tsToInputDate(form.date_due),
  set: (v) => {
    form.date_due = inputDateToTs(v);
  },
});

const successDateInput = computed({
  get: () => tsToInputDate(form.date_successful_lead),
  set: (v) => {
    form.date_successful_lead = inputDateToTs(v);
  },
});

watch(
  () => form.successful_lead,
  (v) => {
    if (!v) form.date_successful_lead = null;
  }
);

// -------------------- Image upload (thumbnail) --------------------
const tempImage = ref(null);

watch(
  () => tempImage.value?.url,
  (newUrl, oldUrl) => {
    if (oldUrl && oldUrl.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(oldUrl);
      } catch (e) {}
    }
  }
);

function onSingleImageSelected(e) {
  if (props.readOnly) return;

  const file = (e.target.files || [])[0] || null;

  form.image = file;

  if (file) form.remove_image = false;

  tempImage.value = file
    ? { name: file.name, url: URL.createObjectURL(file) }
    : null;

  if (file) form.clearErrors("image");
}

function clearSelectedTempImage() {
  if (props.readOnly) return;
  form.image = null;
  tempImage.value = null;
}

function markRemoveCurrentImage() {
  if (props.readOnly) return;
  form.remove_image = true;
  form.image = null;
  tempImage.value = null;
  form.clearErrors("image");
}

function undoRemoveCurrentImage() {
  if (props.readOnly) return;
  form.remove_image = false;
}

// -------------------- Navigation --------------------
const goBack = () => {
  if (typeof history !== "undefined" && history.length > 1) history.back();
  else
    Inertia.get(
      route("sales.salesplan.index"),
      {},
      { preserveState: true, preserveScroll: true }
    );
};

// -------------------- Save --------------------
const saveForm = () => {
  if (props.readOnly) return;

  const required = [
    { key: "employeeID", label: "Project manager", el: "employeeID" },
    { key: "client", label: "Client", el: "client" },
    { key: "contact_name", label: "Contact name", el: "contact_name" },
    { key: "contact_number", label: "Contact phone", el: "contact_number" },
    { key: "start_date", label: "Start date", el: "start_date" },
    { key: "notes", label: "Notes", el: "notes" },
  ];

  required.forEach((f) => form.clearErrors(f.key));
  form.clearErrors("image");

  let firstInvalid = null;

  required.forEach(({ key, label, el }) => {
    const v = form[key];
    const missing =
      v === null ||
      v === undefined ||
      (typeof v === "string" ? v.trim() === "" : v === "");
    if (missing) {
      form.setError(key, `${label} is required.`);
      if (!firstInvalid) firstInvalid = el;
    }
  });

  const missingImage =
    !form.image && (!props.lead?.thumbnail || form.remove_image);

  if (missingImage) {
    form.setError("image", "Thumbnail is required.");
    if (!firstInvalid) firstInvalid = "image-input";
  }

  if (firstInvalid) {
    document
      .getElementById(firstInvalid)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
    return;
  }

  saving.value = true;

  const endpoint = !!props.lead?.id
    ? route("sales.salesplan.update", { salesLead: props.lead.id })
    : route("sales.salesplan.store");

  form.post(endpoint, {
    preserveScroll: true,
    forceFormData: true,
    onFinish() {
      saving.value = false;
    },
  });
};
</script>

<template>
  <AppLayout title="Sales Lead">
    <template #title>
      <a href="#" @click.prevent="goBack">
        <SecondaryButton>Back</SecondaryButton>
      </a>
    </template>

    <div class="max-w-7xl mx-auto py-5 sm:px-6 lg:px-8">
      <FormSection>
        <template #title>
          <a href="#" @click.prevent="goBack">
            <SecondaryButton>Back</SecondaryButton>
          </a>
        </template>

        <template #form>
          <!-- Project Manager -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Project manager" id="employeeID" :required="true" />
            <SearchSelect
              v-model="pmModel"
              :options="pmOptions"
              placeholder="Select a project manager"
              :disabled="readOnly"
            />
            <InputError :message="form.errors.employeeID" class="mt-2" />
          </div>

          <!-- Client -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Client" id="client" :required="true" />
            <TextInput
              id="client"
              v-model="form.client"
              type="text"
              class="mt-1 block w-full"
              :disabled="readOnly"
            />
            <InputError :message="form.errors.client" class="mt-2" />
          </div>

          <!-- Contact name -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Contact name" id="contact_name" :required="true" />
            <TextInput
              id="contact_name"
              v-model="form.contact_name"
              type="text"
              class="mt-1 block w-full"
              :disabled="readOnly"
            />
            <InputError :message="form.errors.contact_name" class="mt-2" />
          </div>

          <!-- Contact surname -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Contact surname" id="contact_surname" />
            <TextInput
              id="contact_surname"
              v-model="form.contact_surname"
              type="text"
              class="mt-1 block w-full"
              :disabled="readOnly"
            />
            <InputError :message="form.errors.contact_surname" class="mt-2" />
          </div>

          <!-- Contact phone -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Contact phone" id="contact_number" :required="true" />

            <TextInput
              v-if="readOnly"
              id="contact_number"
              v-model="form.contact_number"
              type="text"
              class="mt-1 block w-full"
              :disabled="true"
            />

            <PhoneInput
              v-else
              v-model="form.contact_number"
              :disabled="readOnly"
            />

            <InputError :message="form.errors.contact_number" class="mt-2" />
          </div>

          <!-- Contact email -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Contact email" id="contact_email" />
            <TextInput
              id="contact_email"
              v-model="form.contact_email"
              type="email"
              class="mt-1 block w-full"
              :disabled="readOnly"
            />
            <InputError :message="form.errors.contact_email" class="mt-2" />
          </div>

          <!-- Start date -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Start date" :required="true" />
            <TextInput
              id="start_date"
              v-model="startDateInput"
              type="date"
              class="mt-1 block w-full"
              :disabled="readOnly"
            />
            <InputError :message="form.errors.start_date" class="mt-2" />
          </div>

          <!-- Due date -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Due date" />
            <TextInput
              id="date_due"
              v-model="dateDueInput"
              type="date"
              class="mt-1 block w-full"
              :disabled="readOnly"
            />
            <InputError :message="form.errors.date_due" class="mt-2" />
          </div>

          <!-- Successful lead -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Successful lead" />
            <div class="mt-2 flex items-center gap-2">
              <input
                id="successful_lead"
                type="checkbox"
                class="rounded border-gray-300"
                v-model="form.successful_lead"
                :disabled="readOnly"
              />
              <label for="successful_lead" class="text-sm text-gray-700">Closed sale</label>
            </div>
            <InputError :message="form.errors.successful_lead" class="mt-2" />
          </div>

          <!-- Successful lead date -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Successful lead date" />
            <TextInput
              id="date_successful_lead"
              v-model="successDateInput"
              type="date"
              class="mt-1 block w-full"
              :disabled="readOnly || !form.successful_lead"
            />
            <InputError :message="form.errors.date_successful_lead" class="mt-2" />
          </div>

          <!-- Action plan -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Action plan" />
            <textarea
              rows="8"
              id="action_plan"
              v-model="form.action_plan"
              class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full disabled:bg-gray-50"
              :disabled="readOnly"
            />
            <InputError :message="form.errors.action_plan" class="mt-2" />
          </div>

          <!-- Notes -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Notes" :required="true" />
            <textarea
              rows="8"
              id="notes"
              v-model="form.notes"
              class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full disabled:bg-gray-50"
              :disabled="readOnly"
            />
            <InputError :message="form.errors.notes" class="mt-2" />
          </div>
        </template>
      </FormSection>

      <br />

      <!-- Thumbnail -->
      <FormSection :noGrid="true">
        <template #title>Thumbnail <span class="text-red-500">*</span></template>

        <template #form>
          <div v-if="lead?.id" class="space-y-3">
            <div class="flex items-start gap-4">
              <div class="w-36">
                <img
                  v-if="lead.url && !form.remove_image"
                  :src="lead.url"
                  class="h-28 w-28 object-cover rounded border"
                  :alt="lead.thumbnail"
                />
                <div v-else class="h-28 w-28 rounded border flex items-center justify-center text-gray-400">—</div>
              </div>

              <div class="flex-1 space-y-2">
                <div class="text-sm text-gray-700">
                  <span class="font-semibold">Current:</span>
                  <span v-if="lead.thumbnail && !form.remove_image">{{ lead.thumbnail }}</span>
                  <span v-else class="text-gray-500">No image</span>
                </div>

                <div class="flex gap-2">
                  <button
                    type="button"
                    class="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                    @click="markRemoveCurrentImage"
                    :disabled="readOnly || !lead.thumbnail || form.remove_image"
                    title="Remove current thumbnail"
                  >
                    Remove image
                  </button>

                  <button
                    v-if="form.remove_image"
                    type="button"
                    class="px-3 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                    @click="undoRemoveCurrentImage"
                    :disabled="readOnly"
                    title="Undo remove"
                  >
                    Undo
                  </button>
                </div>

                <p v-if="form.remove_image" class="text-sm text-amber-700">
                  Thumbnail will be removed when you click <b>Save</b>.
                </p>
              </div>
            </div>
          </div>

          <div v-if="!readOnly" class="space-y-3" :class="lead?.id ? 'mt-6' : ''">
            <InputLabel
              :value="lead?.id ? 'Replace thumbnail (only 1)' : 'Upload thumbnail (only 1)'"
              :required="true"
            />

            <div class="flex justify-center items-center w-full">
              <label
                for="image-input"
                class="flex flex-col justify-center items-center w-full h-32 rounded-lg border-2 border-dashed cursor-pointer hover:bg-gray-100 border-gray-300"
                :class="readOnly ? 'opacity-60 cursor-not-allowed' : ''"
              >
                <div class="flex flex-col justify-center items-center pt-5 pb-6">
                  <p class="mb-2 text-sm text-gray-500">
                    <span class="font-semibold">Click to upload</span> (1 image)
                  </p>
                  <p class="text-xs text-gray-500">Max. File Size: 10MB</p>
                </div>

                <input
                  id="image-input"
                  type="file"
                  accept="image/*"
                  @change="onSingleImageSelected"
                  class="hidden"
                  :disabled="readOnly"
                />
              </label>
            </div>

            <div class="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4" v-if="tempImage">
              <div class="relative rounded-lg p-2 bg-white shadow-sm">
                <img :src="tempImage.url" alt="" class="w-full h-32 object-cover rounded-md" />
                <div class="mt-1 text-xs truncate text-center">{{ tempImage.name }}</div>
                <button
                  type="button"
                  class="absolute top-1 right-1 bg-white/80 rounded-full p-1 hover:bg-white"
                  @click="clearSelectedTempImage"
                  :disabled="readOnly"
                  title="Remove selected image"
                >
                  <span class="fa fa-trash text-red-600 text-sm"></span>
                </button>
              </div>
            </div>

            <InputError :message="form.errors.image" class="mt-2" />
          </div>
        </template>
      </FormSection>

      <br />

      <FormSection :noGrid="true">
        <template #actions>
          <a href="#" class="mr-5" v-if="!saving" @click.prevent="goBack">
            <SecondaryButton type="button">Back</SecondaryButton>
          </a>

          <PrimaryButton
            v-if="!readOnly"
            type="button"
            @click.prevent="saveForm"
            :disabled="saving"
          >
            Save
          </PrimaryButton>
        </template>
      </FormSection>
    </div>
  </AppLayout>
</template>
